import type React from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { Html } from 'src/app/shared/components/html';

import {
	changeDotOrgCaptionStyles,
	petitionTitleStyles,
	postTitleContainerStyles,
	profileContainerStyles,
} from './PostPreview.styles';
import { htmlContainerStyles, mainContainerStyles } from './YourPost.styles';

type YourPostProps = {
	isExpanded: boolean;
	postText?: string;
	imageUrl?: string | null;
	previewTitle?: string;
};

export const YourPost: React.FC<YourPostProps> = ({ isExpanded, postText, imageUrl, previewTitle }) => {
	const { translate } = useI18n();
	const { resolveUrl } = useUtilityContext();

	return (
		<section
			data-testid="preview-post-layout"
			role="group"
			aria-label={
				isExpanded
					? translate(
							'corgi.petition.starter_dashboard_v2.ready_to_share.share_preview_modal.post_preview.expanded_aria_label',
						)
					: translate('corgi.petition.starter_dashboard_v2.ready_to_share.share_preview_modal.post_preview.aria_label')
			}
			className={mainContainerStyles({ isExpanded })}
		>
			<div className="flex flex-col gap-3">
				<div className="px-1.5">
					{/* Not using the Image component as this image doesn't have a supported aspect ratio */}
					<img
						src="https://static.change.org/images/pre-made-post-modal-mock-post-title.png"
						alt={translate(
							'corgi.petition.starter_dashboard_v2.ready_to_share.share_preview_modal.post_preview.post_title_alt',
						)}
						className={profileContainerStyles({ isExpanded })}
					/>
					<Html as="pre" html={postText || ''} className={htmlContainerStyles({ isExpanded })} />
				</div>
				<div>
					{/* Not using the Image component as this image doesn't have a supported aspect ratio */}
					<img
						src={imageUrl ? resolveUrl(imageUrl) : undefined}
						alt={translate(
							'corgi.petition.starter_dashboard_v2.ready_to_share.share_preview_modal.post_preview.preview_link_alt',
						)}
						className="w-full"
					/>
					<div className={postTitleContainerStyles({ isExpanded })}>
						<span className={changeDotOrgCaptionStyles({ isExpanded })}>
							{translate(
								'corgi.petition.starter_dashboard_v2.ready_to_share.share_preview_modal.post_preview.change_dot_org_caption',
							)}
						</span>
						<span className={petitionTitleStyles({ isExpanded })}>{previewTitle}</span>
					</div>
				</div>
			</div>
			{/* The following images are only visible when the component is *expanded* in the browser */}
			<img
				src="https://static.change.org/images/pre-made-post-modal-mock-post.png?V2"
				alt={translate(
					'corgi.petition.starter_dashboard_v2.ready_to_share.share_preview_modal.post_preview.post_background_alt',
				)}
				className="h-full w-full object-cover"
			/>
			<img
				src="https://static.change.org/images/pre-made-post-modal-mock-post.png?V2"
				alt={translate(
					'corgi.petition.starter_dashboard_v2.ready_to_share.share_preview_modal.post_preview.post_background_alt',
				)}
				className="h-full w-full object-cover"
			/>
		</section>
	);
};
